import { ComponentProps, ReactElement, RefObject, useMemo } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { GridFilterPanel } from '@mui/x-data-grid-pro';
import { getFilterPanelRootSx } from '../getFilterPanelRootSx';
import { UseFilterPresetsResult } from '../hooks/useFilterPresets';
import {
  filterPanelFormsAreaSx,
  filterPanelFormsAreaReadonlySx,
  filterPanelLayoutSx,
} from '../styles';
import { FilterFormsComponent } from '../types';
import { FilterPanelFormsHeader } from './FilterPanelFormsHeader';
import { FilterPresetsSidebar } from './FilterPresetsSidebar';

type FilterPanelLayoutProps = {
  formsAreaRef: RefObject<HTMLDivElement>;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  presetState: UseFilterPresetsResult;
  excludeFieldFromCount?: string;
  filterPanelSx?: SxProps<Theme>;
  FilterForms: FilterFormsComponent;
  gridFilterPanelProps: ComponentProps<typeof GridFilterPanel>;
};

export function FilterPanelLayout({
  formsAreaRef,
  sidebarCollapsed,
  onToggleSidebar,
  presetState,
  excludeFieldFromCount,
  filterPanelSx,
  FilterForms,
  gridFilterPanelProps,
}: FilterPanelLayoutProps): ReactElement {
  const isReadonly = presetState.activePreset?.isStatic === true;
  const formsAreaSx = isReadonly ? filterPanelFormsAreaReadonlySx : filterPanelFormsAreaSx;

  const filterPanelRootSx = useMemo(
    () => getFilterPanelRootSx(filterPanelSx),
    [filterPanelSx]
  );

  return (
    <Box sx={filterPanelRootSx}>
      <Box sx={filterPanelLayoutSx}>
        <FilterPresetsSidebar
          presets={presetState.presets}
          activePresetId={presetState.activePresetId}
          isModified={presetState.isModified}
          hasUnsavedFilters={presetState.hasUnsavedFilters}
          excludeFieldFromCount={excludeFieldFromCount}
          collapsed={sidebarCollapsed}
          onToggleCollapse={onToggleSidebar}
          onSelectPreset={presetState.selectPreset}
          onDeletePreset={presetState.deletePreset}
          onOpenRenameDialog={presetState.openRenameDialog}
          onSaveChanges={presetState.saveChangesToPreset}
          onDiscardChanges={presetState.discardChanges}
        />

        <Box ref={formsAreaRef} sx={formsAreaSx}>
          <FilterPanelFormsHeader
            activePreset={presetState.activePreset}
            isModified={presetState.isModified}
            hasUnsavedFilters={presetState.hasUnsavedFilters}
            onOpenCreateDialog={presetState.openCreateDialog}
            onSaveChanges={presetState.saveChangesToPreset}
            onDiscardChanges={presetState.discardChanges}
          />
          <FilterForms {...gridFilterPanelProps} />
        </Box>
      </Box>
    </Box>
  );
}
